import React, { useState } from "react"
import styled, { ThemeConsumer } from "styled-components"
import Button from "components/Button" // Make sure this is the correct path to your Button component
import {
  CW20_ADDR,
  STAKING_OPTIONS,
  STAKE_FACTORY_ADDR,
  CW20_SYMBOL,
  SALES_ADDR,
} from "constants/constants"

import {
  Coin,
  Coins,
  CreateTxOptions,
  Fee,
  Numeric,
} from "@terra-money/terra.js"

import { MsgExecuteContract } from "@terra-money/terra.js"
import useGasPrice from "rest/useGasPrice"
import { useNetwork, useContract, useAddress, useConnectModal } from "hooks"
import { useLCDClient } from "layouts/WalletConnectProvider"
import { TxResult, useWallet } from "@terra-money/wallet-provider"
import Container from "components/Container"
import Result from "../../forms/Result"

const ManagementPageWrapper = styled.div`
  margin: 20px;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`

const Column = styled.div`
  flex: 1;
  margin-right: 20px;
  &:last-child {
    margin-right: 0;
  }
`

const Label = styled.label`
  margin-bottom: 8px;
  font-size: 18px;
  font-weight: bold;
`

const Input = styled.input`
  position: relative; /* Add this line */
  z-index: 1; /* Add this line */
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
`

const StyledButton = styled(Button)`
  background-color: ${(props) => (props.clicked ? "#aaa" : "#007bff")};
  pointer-events: ${(props) => (props.clicked ? "none" : "auto")};
  /* Add other styles to indicate the button has been clicked, if desired */
`

const ButtonContainer = styled.div`
  position: relative; /* Add this line */
  z-index: 1; /* Add this line */
  margin-top: 0px;
`

const Title = styled.h1`
  font-size: 36px;
  font-weight: bold;
  color: #f0f0f0; // Change the color to fit your design
  margin-bottom: 20px; // Spacing below the title
  text-align: center; // This will center the title text
`

const ManagementPage = () => {
  const [data, setData] = useState([
    { label: "Token Price (in LUNC)", value: "" },
    { label: "Min Buy Quantity", value: "" },
    { label: "Deposit into Sales", value: "" },
    { label: "Withdraw All from Sales", value: "" },
    { label: "Deposit into Factory", value: "" },
    { label: "Withdraw All from Factory", value: "" },
  ])
  const [clickedButtonIndex, setClickedButtonIndex] = useState<number | null>(
    null
  )
  const [bkChainMsg, setBkChainMsg] = useState<[]>([])
  const [wrappedBkChainMsg, setWrappedBkChainMsg] = useState<[]>([])
  const [msgs, setMsgs] = useState<MsgExecuteContract | MsgExecuteContract[]>(
    []
  )
  const [result, setResult] = useState<TxResult | undefined>()

  const wallet = useWallet()
  const walletAddress = useAddress()
  const { gasPrice } = useGasPrice("uluna")
  const { terra } = useLCDClient()

  const handleInputChange = (index: number, value: any) => {
    // Remove any characters that are not numbers or a single decimal point at the beginning
    let newValue = value.replace(/[^0-9.]/g, "")

    // Handle cases with multiple periods (.)
    const parts = newValue.split(".")
    if (parts.length > 2) {
      newValue = parts[0] + "." + parts.slice(1).join("")
    }

    // Prevent multiple leading zeros
    if (
      newValue.startsWith("0") &&
      newValue[1] !== "." &&
      newValue.length > 1
    ) {
      newValue = newValue.slice(1)
    }

    // Set the updated value
    const newData = [...data]
    newData[index].value = newValue // Store newValue, not the original value
    setData(newData)
  }

  const handleButtonClick = (index: number) => {
    setClickedButtonIndex(index)

    // build transaction
    /////////////////////////////////////////////////////////////////////////////
    //  Set Token Price
    /////////////////////////////////////////////////////////////////////////////
    if (index === 0) {
      //
      // Retrieve the value from the input box
      const inputValue = data[0].value
      // Convert the value to a number and multiply by 1,000,000
      const tokenPrice = Number(inputValue) * 1000000
      // Check if the input value is valid (non-empty and a valid number)
      if (!isNaN(tokenPrice) && walletAddress && inputValue.trim() !== "") {
        const executeMessage = {
          set_price: { denom: "uluna", price: tokenPrice.toString() },
        }

        const carrierMessage = new MsgExecuteContract(
          walletAddress,
          SALES_ADDR,
          executeMessage
        )

        executeChainMsg(carrierMessage)
      } else {
        if (!walletAddress) {
          data[0].value = "Connect Wallet"
        } else {
          data[0].value = "Not valid"
        }
      }
    }

    /////////////////////////////////////////////////////////////////////////////
    //  Set Min Buy
    /////////////////////////////////////////////////////////////////////////////
    else if (index === 1) {
      // Buy Tokens
      // Retrieve the value from the input box
      const inputValue = data[1].value
      // Convert the value to a number and multiply by 1,000,000
      const minBuy = Number(inputValue) * 1000000
      // Check if the input value is valid (non-empty and a valid number)
      if (!isNaN(minBuy) && walletAddress && inputValue.trim() !== "") {
        const executeMessage = {
          update_min_buy: { min_stake: minBuy.toString() },
        }

        const carrierMessage = new MsgExecuteContract(
          walletAddress,
          STAKE_FACTORY_ADDR,
          executeMessage
        )

        executeChainMsg(carrierMessage)
      } else {
        if (!walletAddress) {
          data[1].value = "Connect Wallet"
        } else {
          data[1].value = "Not valid"
        }
      }
    }

    /////////////////////////////////////////////////////////////////////////////
    // Deposit CW20 Tokens (sales)
    /////////////////////////////////////////////////////////////////////////////
    else if (index === 2) {
      // Retrieve the value from the input box
      const inputValue = data[2].value
      // Convert the value to a number and multiply by 1,000,000
      const depositAmount = Number(inputValue) * 1000000
      // Check if the input value is valid (non-empty and a valid number)
      if (!isNaN(depositAmount) && walletAddress && inputValue.trim() !== "") {
        const executeMessage = {
          send: {
            amount: depositAmount.toString(),
            contract: SALES_ADDR,
            msg: "",
          },
        }

        const carrierMessage = new MsgExecuteContract(
          walletAddress,
          CW20_ADDR,
          executeMessage
        )

        executeChainMsg(carrierMessage)
      } else {
        if (!walletAddress) {
          data[2].value = "Connect Wallet"
        } else {
          data[2].value = "Not valid"
        }
      }
    }

    /////////////////////////////////////////////////////////////////////////////
    // Withdraw all CW20 (sales)
    /////////////////////////////////////////////////////////////////////////////
    else if (index === 3) {
      const executeMessage = {
        withdraw_cw20: {},
      }

      const carrierMessage = new MsgExecuteContract(
        walletAddress,
        SALES_ADDR,
        executeMessage
        //{ uluna: {"0"}}
      )

      executeChainMsg(carrierMessage)
    }

    /////////////////////////////////////////////////////////////////////////////
    // Deposit CW20 Tokens (factory)
    /////////////////////////////////////////////////////////////////////////////
    else if (index === 4) {
      // Retrieve the value from the input box
      const inputValue = data[4].value
      // Convert the value to a number and multiply by 1,000,000
      const depositAmount = Number(inputValue) * 1000000
      // Check if the input value is valid (non-empty and a valid number)
      if (!isNaN(depositAmount) && walletAddress && inputValue.trim() !== "") {
        const executeMessage = {
          send: {
            amount: depositAmount.toString(),
            contract: STAKE_FACTORY_ADDR,
            msg: btoa(
              JSON.stringify({
                staker_addr: "",
                staking_days: 0,
                description: "",
              })
            ),
          },
        }

        const carrierMessage = new MsgExecuteContract(
          walletAddress,
          CW20_ADDR,
          executeMessage
        )

        executeChainMsg(carrierMessage)
      } else {
        if (!walletAddress) {
          data[4].value = "Connect Wallet"
        } else {
          data[4].value = "Not valid"
        }
      }
    }

    /////////////////////////////////////////////////////////////////////////////
    // Withdraw all CW20 (factory)
    /////////////////////////////////////////////////////////////////////////////
    else if (index === 5) {
      const executeMessage = {
        withdraw_all: {},
      }

      const carrierMessage = new MsgExecuteContract(
        walletAddress,
        STAKE_FACTORY_ADDR,
        executeMessage
        //{ uluna: {"0"}}
      )

      executeChainMsg(carrierMessage)
    }

    /////////////////////////////////////////////////////////////////////////////
    // End options
    /////////////////////////////////////////////////////////////////////////////

    setTimeout(() => {
      setClickedButtonIndex(null) // Reset after a short delay
    }, 250) // Delay in milliseconds
  }

  /////////////////////////////////////////////////////////////////////////////
  // Send Transaction
  /////////////////////////////////////////////////////////////////////////////
  const executeChainMsg = async (msg: MsgExecuteContract) => {
    try {
      let msgs: any[] = [msg] // Adjust type if necessary

      // Calculate the total LUNC being sent from the msgs
      const luncAmountSent = getTotalLuncAmount(msgs)

      // Additional fee in LUNC (30 LUNC)
      const additionalLuncFee = 30 * 1e6 // 30 LUNC in micro LUNC

      const chain_tax = luncAmountSent * 0.0051

      // Total fee amount in micro LUNC
      const totalFeeAmount = (chain_tax + additionalLuncFee).toString() // Convert to string

      // Create the Fee object
      const fee = new Fee(
        500000,
        new Coins([new Coin("uluna", totalFeeAmount)])
      )

      let txOptions: CreateTxOptions = {
        msgs,
        memo: "",
        // Adjust gasPrice, feeSymbol, and tax if necessary
        gasPrices: `${gasPrice}${"uluna"}`,
        gasAdjustment: "1.8",
        feeDenoms: ["uluna"], // Specify the fee denomination
        fee: fee,
        // This works, but hardcoded
        //fee:  new Fee(500000, new Coins([new Coin("uluna", "1")]))
        //fee: new Fee(0.5, new Coins([new Coin("uluna", (0.5 * parseFloat(`30000000uluna`)).toFixed(0))])), // Calculate fee amount
      }

      const signMsg = await terra.tx.create(
        [{ address: walletAddress }],
        txOptions
      )
      //let fee = signMsg.auth_info.fee.amount
      //let biggerFee = new Coin("uluna", "0")
      //biggerFee = new Coin("uluna", Math.floor(Number(fee.get("uluna")?.amount) * 3.0))
      //txOptions.fee = new Fee(500000, new Coins([biggerFee])) // Force gas_limit to 500,000

      const extensionResult = await wallet.post(
        {
          ...txOptions,
        },
        walletAddress
      )

      if (extensionResult) {
        setResult(extensionResult)
        return
      }
    } catch (error) {
      console.error(error)
      setResult(error as any)
      window.location.reload()
    }
  }

  // Function to calculate the total LUNC amount being sent
  function getTotalLuncAmount(msgs: MsgExecuteContract[]): number {
    let totalLuncAmount = 0

    for (const msg of msgs) {
      // Use the get method to access the amount of uluna
      const coin = msg.coins.get("uluna")
      if (coin) {
        totalLuncAmount += parseInt(coin.amount.toString(), 10)
      }
    }

    return totalLuncAmount
  }

  const handleFailure = () => {
    setTimeout(() => {
      setResult(undefined)
      window.location.reload()
    }, 5000) // Adjust the timeout to 3000 milliseconds (3 seconds)
  }

  return (
    <ManagementPageWrapper>
      {result && (
        <Container sm>
          <Result
            response={result}
            error={result instanceof Error ? result : undefined}
            parserKey={"default"}
            onFailure={handleFailure}
          />
        </Container>
      )}
      <Title>Manage Smart Contract</Title> {/* Add this line for the title */}
      {data.map((item, index) => (
        <Row key={index}>
          <Column>
            <Label>{item.label}</Label>
          </Column>
          <Column>
            {index !== 3 && index !== 5 ? ( // This checks if it's not the fourth row
              <Input
                type="text"
                value={item.value}
                onChange={(e) => handleInputChange(index, e.target.value)}
              />
            ) : null}
          </Column>
          <Column>
            <ButtonContainer>
              <StyledButton
                onClick={() => handleButtonClick(index)}
                clicked={clickedButtonIndex === index}
              >
                {clickedButtonIndex === index ? "Clicked!" : "Send"}
              </StyledButton>
            </ButtonContainer>
          </Column>
        </Row>
      ))}
    </ManagementPageWrapper>
  )
}

export default ManagementPage
